import {
  KdsBadge,
  KdsButtonIcon,
  KdsFocusableElement,
  KdsLink,
  KdsModal,
  KdsText,
} from "@kazaar-platform/design-system";
import {
  RiArrowLeftSLine,
  RiArrowRightSLine,
  RiEyeLine,
} from "@remixicon/react";
import clsx from "clsx";
import { size, uniqueId } from "lodash";
import {
  type KeyboardEvent,
  type MouseEvent,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { UserEntityPreferredLanguage } from "@/api/__generated__/orval/auth";
import { formatPrice } from "@/helpers/formatPrice";
import { type CardQuoteProps } from "./CardQuote.types";

export const CardQuote = ({
  className,
  quoteNumber,
  supplier,
  fileUrl = undefined,
  totalPrice,
  deleteLabel,
  unitPriceLabel,
  totalPriceLabel,
  currency,
  quantity,
  locale = UserEntityPreferredLanguage.fr,
  isNotSelectable = false,
  selected,
  itemsRelatedLabel,
  itemsRelated = [],
  multiItemsLabel,
  onSelect,
  onDelete,
  modalProps,
}: CardQuoteProps) => {
  // State variables
  const [isOpen, setIsOpen] = useState(false);
  const [scrollingArrows, setScrollingArrow] = useState<{
    leftArrow: boolean;
    rightArrow: boolean;
  }>({
    leftArrow: false,
    rightArrow: false,
  });
  // Ref for the badge container to handle the scroll behaviour
  const badgeContainerRef = useRef<HTMLDivElement>(null);

  const numberOfItems = size(itemsRelated);

  // Handle click and keyboard events
  const handleInteraction = (e: MouseEvent | KeyboardEvent) => {
    if ("key" in e && e.key !== "Enter" && e.key !== " ") {
      return;
    }
    if ("key" in e && e.key === " ") {
      e.preventDefault(); // Prevent scrolling on Space
    }
    onSelect?.();
  };

  // Define Wrapper ClassNames
  const wrapperClasses = clsx(
    "flex flex-col items-start gap-6 w-full bg-white border border-slate-200 py-6 rounded-2xl rounded transition duration-300 outline-none",
    className,
    selected && !isNotSelectable && "outline-2 outline-offset-2 outline-primary"
  );

  // Define Radio ClassNames
  const radioClasses = clsx(
    "size-4 rounded-full bg-white transition duration-300 mt-1 mr-4",
    selected
      ? "shadow-[inset_0_0_0_4px] shadow-primary"
      : "shadow-[inset_0_0_0_1px] shadow-slate-300"
  );

  // Add event listener on scroll for the badge container
  useEffect(() => {
    badgeContainerRef.current?.addEventListener("scroll", () => {
      if (badgeContainerRef?.current?.scrollLeft === 0) {
        setScrollingArrow({ leftArrow: false, rightArrow: true });
      } else {
        setScrollingArrow({ leftArrow: true, rightArrow: false });
      }
    });

    return () => {
      badgeContainerRef.current?.removeEventListener("scroll", () => {});
    };
  }, []);

  // Check if the badge container is scrollable to set the right arrow
  useLayoutEffect(() => {
    const checkArrows = () => {
      const el = badgeContainerRef.current;
      if (!el) {
        return;
      }

      const isOverflowing = el.scrollWidth > el.clientWidth;

      setScrollingArrow((prev) => {
        if (prev.rightArrow === isOverflowing) {
          return prev;
        }
        return { leftArrow: false, rightArrow: isOverflowing };
      });
    };

    checkArrows();

    const observer = new ResizeObserver(() => {
      checkArrows();
    });

    if (badgeContainerRef.current) {
      observer.observe(badgeContainerRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <KdsFocusableElement
      className={wrapperClasses}
      role={!isNotSelectable ? "button" : "none"}
      tabIndex={!isNotSelectable ? 0 : undefined}
      onClick={!isNotSelectable ? handleInteraction : undefined}
      onKeyDown={!isNotSelectable ? handleInteraction : undefined}
    >
      <div className="flex w-full flex-col px-6">
        <div className="flex">
          {!isNotSelectable && <div className={radioClasses} />}

          <div className="flex flex-1 flex-col">
            <div className="flex justify-between gap-2">
              <div className="flex flex-col">
                <KdsText as="h3" size="subtitle-md">
                  {supplier}
                </KdsText>
                <KdsText>{quoteNumber}</KdsText>
              </div>

              <div>
                {fileUrl && (
                  <KdsLink download href={fileUrl} target="_blank">
                    <KdsButtonIcon
                      color="neutral"
                      variant="outlined"
                      icon={RiEyeLine}
                    />
                  </KdsLink>
                )}
              </div>
            </div>

            <div className="mt-6 flex flex-col">
              <div
                className={clsx(
                  "hidden items-center gap-2",
                  itemsRelated.length === 1 && "flex"
                )}
              >
                <KdsText>{unitPriceLabel} :</KdsText>

                <KdsText size="subtitle-sm">
                  {formatPrice({
                    price: totalPrice / (quantity || 1),
                    maxDigits: 5,
                    locale: locale,
                    currency: currency as string,
                  })}
                </KdsText>
              </div>

              <div className="flex items-center justify-between gap-2">
                <KdsText>{totalPriceLabel}</KdsText>

                <KdsText size="subtitle-sm">
                  {formatPrice({
                    price: totalPrice,
                    locale: locale,
                    currency: currency as string,
                  })}
                </KdsText>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="w-full border-t border-slate-200" />

      <div className="w-full">
        <div className="pl-6">
          <KdsText size="subtitle-xs">{itemsRelatedLabel}</KdsText>
        </div>

        <div
          ref={badgeContainerRef}
          className="hidden-scrollbar w-full overflow-auto px-6"
        >
          <div className="mt-2 flex w-fit flex-row items-center gap-2">
            {/* Left Arrow to scroll left when the container is scrollable */}
            <KdsFocusableElement
              onClick={() =>
                badgeContainerRef.current?.scroll({
                  behavior: "smooth",
                  left: -badgeContainerRef?.current?.clientWidth,
                  top: 0,
                })
              }
              className={clsx(
                "absolute left-[2px] flex h-10 cursor-pointer items-center rounded-e backdrop-blur-sm",
                !scrollingArrows.leftArrow ? "invisible" : "visible"
              )}
            >
              <RiArrowLeftSLine className="text-text-body" />
            </KdsFocusableElement>

            {itemsRelated.slice(0, 3).map((item) => (
              <KdsBadge state="info" key={item}>
                {item}
              </KdsBadge>
            ))}

            {numberOfItems > 3 && (
              <div className="flex min-w-fit flex-row items-center gap-1">
                <KdsText>+</KdsText>
                <KdsLink
                  state="neutral"
                  size="sm"
                  onPress={() => setIsOpen(true)}
                >
                  {`${numberOfItems - 3} ${multiItemsLabel ?? ""}`}
                </KdsLink>
              </div>
            )}

            {/* Right Arrow to scroll right when the container is scrollable */}
            <KdsFocusableElement
              onClick={() =>
                badgeContainerRef.current?.scroll({
                  behavior: "smooth",
                  left: badgeContainerRef?.current?.clientWidth,
                  top: 0,
                })
              }
              className={clsx(
                "absolute right-[2px] flex h-10 cursor-pointer items-center rounded-s backdrop-blur",
                !scrollingArrows.rightArrow ? "invisible" : "visible"
              )}
            >
              <RiArrowRightSLine className="text-text-body" />
            </KdsFocusableElement>
          </div>
        </div>
      </div>

      {!!deleteLabel && !!onDelete && (
        <div className="flex w-full flex-1 justify-end px-6">
          <KdsLink size="sm" state="neutral" onPress={onDelete}>
            {deleteLabel}
          </KdsLink>
        </div>
      )}

      {modalProps && (
        <KdsModal
          isOpen={isOpen}
          onOpenChange={setIsOpen}
          size="md"
          dialogProps={{
            dialogHeaderProps: {
              title: modalProps.title,
            },
            content: (
              <div className="flex h-full w-full flex-wrap items-center gap-2">
                {itemsRelated.map((item) => (
                  <KdsBadge state="neutral" key={uniqueId()}>
                    {item}
                  </KdsBadge>
                ))}
              </div>
            ),
            dialogFooterProps: {
              type: "simple",
              buttonProps: [
                {
                  children: modalProps.closeLabel,
                  variant: "ghost",
                  color: "neutral",
                  onPress: () => setIsOpen(false),
                },
              ],
            },
          }}
        />
      )}
    </KdsFocusableElement>
  );
};
